// Smart Money Concepts (SMC Pro / MSB Dynamic) Engine for Frontend & Replay
// 100% Faithful Port of TradingView Pine Script v5: "MSB Dynamic + 3TP (no Zone) + Display Window"
// + Classical SMC Structure: Order Blocks (+OB/-OB), Fair Value Gaps (FVG), Market Structure Breaks (BOS/CHoCH)

function getPipMultiplier(price) {
  if (price < 5.0) return 10000.0;     // EURUSD, GBPUSD (0.0001 = 1 pip)
  if (price < 500.0) return 100.0;     // USDJPY (0.01 = 1 pip)
  if (price < 15000.0) return 10.0;    // Gold XAUUSD ($1.00 = 10 pips, 0.1 = 1 pip)
  return 1.0;                          // BTCUSD ($1 = 1 pip)
}

function getMintick(price) {
  if (price < 5.0) return 0.00001;
  if (price < 500.0) return 0.001;
  if (price < 15000.0) return 0.01;
  return 0.1;
}

export function resampleBarsToHTF(bars, targetSeconds) {
  if (!bars || bars.length === 0) return [];
  const resampled = [];
  let currBucket = null;
  let currBar = null;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const t = Number(b.time);
    const bucket = Math.floor(t / targetSeconds) * targetSeconds;

    if (bucket !== currBucket) {
      if (currBar !== null) {
        resampled.push(currBar);
      }
      currBucket = bucket;
      currBar = {
        time: bucket,
        open: Number(b.open),
        high: Number(b.high),
        low: Number(b.low),
        close: Number(b.close),
        volume: Number(b.volume || 0)
      };
    } else {
      if (Number(b.high) > currBar.high) currBar.high = Number(b.high);
      if (Number(b.low) < currBar.low) currBar.low = Number(b.low);
      currBar.close = Number(b.close);
      currBar.volume += Number(b.volume || 0);
    }
  }
  if (currBar !== null) {
    resampled.push(currBar);
  }
  return resampled;
}

export function extractHTFOrderBlocks(htfBars, pivotLen = 3, obLookback = 20) {
  if (!htfBars || htfBars.length < 15) return [];
  const n = htfBars.length;
  const times = htfBars.map((b) => Number(b.time));
  const closes = htfBars.map((b) => Number(b.close));
  const highs = htfBars.map((b) => Number(b.high));
  const lows = htfBars.map((b) => Number(b.low));
  const opens = htfBars.map((b) => Number(b.open));

  const shList = [];
  const slList = [];
  const pl = Math.max(2, Math.min(pivotLen, 10));

  for (let i = pl; i < n - pl; i++) {
    const hi = highs[i];
    const li = lows[i];
    let isHigh = true;
    let isLow = true;
    for (let k = i - pl; k <= i + pl; k++) {
      if (k !== i) {
        if (highs[k] > hi) isHigh = false;
        if (lows[k] < li) isLow = false;
        if (!isHigh && !isLow) break;
      }
    }
    if (isHigh) shList.push({ index: i, time: times[i], price: hi });
    if (isLow) slList.push({ index: i, time: times[i], price: li });
  }

  const rawObs = [];
  let shPtr = 0;
  let slPtr = 0;
  let activeSh = null;
  let activeSl = null;
  let lastHIdx = -1;
  let lastLIdx = -1;

  for (let i = pl * 2; i < n; i++) {
    const c = closes[i];
    const t = times[i];
    const maxIdx = i - pl;

    while (shPtr < shList.length && shList[shPtr].index < maxIdx) {
      activeSh = shList[shPtr];
      shPtr++;
    }
    while (slPtr < slList.length && slList[slPtr].index < maxIdx) {
      activeSl = slList[slPtr];
      slPtr++;
    }

    // Bullish OB on Break of High
    if (activeSh && c > activeSh.price && activeSh.index !== lastHIdx) {
      lastHIdx = activeSh.index;
      const bIdx = i;
      const scanLimit = Math.max(0, bIdx - obLookback);
      let lowestIdx = bIdx - 1;
      let lowestLow = lows[lowestIdx];
      for (let k = bIdx - 1; k >= scanLimit; k--) {
        if (lows[k] < lowestLow) {
          lowestLow = lows[k];
          lowestIdx = k;
        }
      }
      if (lowestIdx >= 0) {
        const hasFvg = (lowestIdx + 2 < n && lows[lowestIdx + 2] > highs[lowestIdx]);
        rawObs.push({
          type: "BULLISH_OB",
          top: Math.max(opens[lowestIdx], closes[lowestIdx]),
          bottom: lows[lowestIdx],
          time: times[lowestIdx],
          formedTime: t,
          formedIdx: i,
          barIndex: lowestIdx,
          hasFvg: hasFvg
        });
      }
    } else if (activeSl && c < activeSl.price && activeSl.index !== lastLIdx) {
      lastLIdx = activeSl.index;
      const bIdx = i;
      const scanLimit = Math.max(0, bIdx - obLookback);
      let highestIdx = bIdx - 1;
      let highestHigh = highs[highestIdx];
      for (let k = bIdx - 1; k >= scanLimit; k--) {
        if (highs[k] > highestHigh) {
          highestHigh = highs[k];
          highestIdx = k;
        }
      }
      if (highestIdx >= 0) {
        const hasFvg = (highestIdx + 2 < n && highs[highestIdx + 2] < lows[highestIdx]);
        rawObs.push({
          type: "BEARISH_OB",
          top: highs[highestIdx],
          bottom: Math.min(opens[highestIdx], closes[highestIdx]),
          time: times[highestIdx],
          formedTime: t,
          formedIdx: i,
          barIndex: highestIdx,
          hasFvg: hasFvg
        });
      }
    }
  }

  // Xác định thời điểm OB bị Mitigated (nến đóng qua body):
  // BULLISH OB  -> nến đầu tiên ĐÓNG CỬA dưới đáy OB (close < ob.bottom)
  // BEARISH OB  -> nến đầu tiên ĐÓNG CỬA trên đỉnh OB (close > ob.top)
  // Rút râu (wick) quét qua KHÔNG tính là mitigated
  for (let j = 0; j < rawObs.length; j++) {
    const ob = rawObs[j];
    const formedI = ob.formedIdx || 0;
    ob.isMitigated = false;
    ob.mitigatedTime = null;

    if (ob.type === "BULLISH_OB") {
      for (let k = formedI + 1; k < n; k++) {
        if (closes[k] < ob.bottom) {
          ob.isMitigated = true;
          ob.mitigatedTime = times[k];
          break;
        }
      }
    } else {
      for (let k = formedI + 1; k < n; k++) {
        if (closes[k] > ob.top) {
          ob.isMitigated = true;
          ob.mitigatedTime = times[k];
          break;
        }
      }
    }
  }

  return rawObs;
}

export function checkHTFConfirmation(side, barIndex, entryPrice, htfOBs, highs, lows, times, bufferPrice, lookbackBars = 24, maxAgeSec = 86400 * 7, htfMode = "touch") {
  if (!htfOBs || htfOBs.length === 0) return { confirmed: false, zone: null };
  const currTime = times[barIndex];

  for (let idx = 0; idx < htfOBs.length; idx++) {
    const ob = htfOBs[idx];
    // 1. OB chưa hình thành tại thời điểm này
    if (ob.formedTime > currTime) continue;
    // 2. OB quá cũ (vượt quá maxAge)
    if (currTime - ob.formedTime > maxAgeSec) continue;
    // 3. OB đã bị nến đóng cửa xuyên qua TRƯỚC thời điểm hiện tại -> hết hiệu lực
    if (ob.isMitigated && ob.mitigatedTime && currTime >= ob.mitigatedTime) continue;

    const obTop = ob.top;
    const obBot = ob.bottom;

    if (htfMode === "price_in_ob") {
      // Strict: entry price phải đang nằm trong (hoặc rất gần) vùng OB tại lúc MSB
      if (side === "SELL" && ob.type === "BEARISH_OB") {
        if (entryPrice >= (obBot - bufferPrice) && entryPrice <= (obTop + bufferPrice)) {
          return { confirmed: true, zone: ob };
        }
      } else if (side === "BUY" && ob.type === "BULLISH_OB") {
        if (entryPrice >= (obBot - bufferPrice) && entryPrice <= (obTop + bufferPrice)) {
          return { confirmed: true, zone: ob };
        }
      }
    } else {
      // Loose (touch): giá chỉ cần từng chạm OB trong lookbackBars nến trước
      let subHigh = -1e10;
      let subLow = 1e10;
      const startIdx = Math.max(0, barIndex - lookbackBars);
      for (let k = startIdx; k <= barIndex; k++) {
        if (highs[k] > subHigh) subHigh = highs[k];
        if (lows[k] < subLow) subLow = lows[k];
      }
      if (side === "SELL" && ob.type === "BEARISH_OB") {
        if (subHigh >= (obBot - bufferPrice) && subLow <= (obTop + bufferPrice)) {
          return { confirmed: true, zone: ob };
        }
      } else if (side === "BUY" && ob.type === "BULLISH_OB") {
        if (subLow <= (obTop + bufferPrice) && subHigh >= (obBot - bufferPrice)) {
          return { confirmed: true, zone: ob };
        }
      }
    }
  }
  return { confirmed: false, zone: null };
}

export function calculateSMC(bars, params = {}) {
  if (!bars || bars.length < 25) {
    const emptyRes = {
      total_trades: 0, win_rate: 0, wins: 0, losses: 0, net_pnl: 0,
      profit_factor: 0, max_win_streak: 0, max_loss_streak: 0, avg_rr: "1:1.0"
    };
    return {
      orderBlocks: [],
      order_blocks: [],
      fvgZones: [],
      fvg_zones: [],
      structureBreaks: [],
      structure_breaks: [],
      swingHighs: [],
      swingLows: [],
      swingPoints: [],
      active_bear_setups: [],
      active_bull_setups: [],
      visual_lines: [],
      orders: [],
      completed_trades: [],
      htf_obs: [],
      marketBias: "NEUTRAL",
      sentimentScore: 0,
      signals: {
        latest_signal: null,
        recent_signals: [],
        all_signals: [],
        chart_markers: [],
        market_bias: "NEUTRAL",
        sentiment_score: 0,
        backtest: emptyRes,
        backtest_windows: { "24h": emptyRes, "3d": emptyRes, "7d": emptyRes, "all": emptyRes },
        streaks: { max_win_streak: 0, max_loss_streak: 0 }
      },
      summary: {
        totalOBs: 0, unmitigatedOBs: 0, totalFVGs: 0, unmitigatedFVGs: 0, totalBreaks: 0, marketBias: "NEUTRAL"
      }
    };
  }

  // Parameter Mapping (matching Pine Script v5 inputs)
  const windowBars = params.windowBars || params.smcWindowBars || 150;
  const lenPH = params.lenPH || params.smcLenPH || params.smcPivotLength || 3;
  const lenPL = params.lenPL || params.smcLenPL || params.smcPivotLength || 3;
  const minGapBars = params.minGapBars !== undefined ? params.minGapBars : 2;
  const epsTicks = params.epsTicks !== undefined ? params.epsTicks : 1;
  const useClose = params.useClose !== undefined ? params.useClose : true;
  const maxSetups = params.maxSetups || 3;
  const r1 = params.smcR1 !== undefined ? Number(params.smcR1) : (params.smc_r1 !== undefined ? Number(params.smc_r1) : (params.r1 !== undefined ? Number(params.r1) : 1.5));
  const r2 = params.smcR2 !== undefined ? Number(params.smcR2) : (params.smc_r2 !== undefined ? Number(params.smc_r2) : (params.r2 !== undefined ? Number(params.r2) : 2.0));
  const lineBars = params.lineBars || 80;
  const tpSlByClose = params.tpSlByClose || false;
  const breakMode = params.breakMode || "%Body";
  const breakBufTicks = params.breakBufTicks || 0;
  const breakBufPct = params.breakBufPct !== undefined ? params.breakBufPct : 10.0;
  const breakAllowEqual = params.breakAllowEqual || false;
  const sameByClose = params.sameByClose || false;
  const refineByFast = params.refineByFast !== undefined ? params.refineByFast : true;
  const waitPivot = params.waitPivot !== undefined ? params.waitPivot : true;
  const revEnable = params.revEnable === true;
  const revMinBars = params.revMinBars || 1;
  const revMaxBars = params.revMaxBars || 60;
  const revBreakMode = params.revBreakMode || "%Body";
  const revBufTicks = params.revBufTicks || 0;
  const revBufPct = params.revBufPct !== undefined ? params.revBufPct : 10.0;
  const revAllowEqual = params.revAllowEqual || false;
  const revByClose = params.revByClose || false;
  const revR = params.revR !== undefined ? Number(params.revR) : 2.0;
  const entryMode = params.entryMode || "Limit 1";
  const midSamePct = params.midSamePct !== undefined ? Number(params.midSamePct) : (params.mid_same_pct !== undefined ? Number(params.mid_same_pct) : 50.0);
  const validMidSamePct = (isNaN(midSamePct) || midSamePct <= 0 || midSamePct > 100) ? 50.0 : midSamePct;
  const msRatio = validMidSamePct / 100;
  const sameRatio = 1 - msRatio;
  const halfBreakBasis = params.halfBreakBasis || "Range(H-L)";
  const fibPct = params.fibPct !== undefined ? Number(params.fibPct) : 35.0;
  const slFilterEnable = params.slFilterEnable !== undefined ? params.slFilterEnable : true;
  const slMinPips = params.slMinPips !== undefined ? Number(params.slMinPips) : (params.slMin !== undefined ? Number(params.slMin) : 10.0);
  const slMaxPips = params.slMaxPips !== undefined ? Number(params.slMaxPips) : (params.slMax !== undefined ? Number(params.slMax) : 150.0);
  const slExtraOn = params.slExtraOn !== undefined ? params.slExtraOn : true;
  const slExtraPips = params.slExtraPips !== undefined ? Number(params.slExtraPips) : (params.slExtraVal !== undefined ? Number(params.slExtraVal) : 5.0);
  const pendingCancelOn = params.pendingCancelOn !== undefined ? params.pendingCancelOn : true;
  const pendingCancelN = params.pendingCancelN || 40;
  const placeNextBar = params.placeNextBar !== undefined ? params.placeNextBar : true;
  const allowSameBarExit = params.allowSameBarExit || false;
  const htfFilterEnable = params.htfFilterEnable !== false && params.htfFilter !== false && params.htf_filter_enable !== false;
  const htfTimeframe = params.htfTimeframe || params.htf_timeframe || "AUTO";
  const htfBufferPips = params.htfBufferPips !== undefined ? Number(params.htfBufferPips) : (params.htf_buffer_pips !== undefined ? Number(params.htf_buffer_pips) : 10.0);
  const htfMaxAgeDays = params.htfMaxAgeDays !== undefined ? Number(params.htfMaxAgeDays) : (params.htf_max_age_days !== undefined ? Number(params.htf_max_age_days) : 7);
  const htfMode = params.htfMode || params.htf_mode || "touch";
  const breakevenEnable = params.breakevenEnable === true || params.breakeven_enable === true;
  const breakevenPips = params.breakevenPips !== undefined ? Number(params.breakevenPips) : (params.breakeven_pips !== undefined ? Number(params.breakeven_pips) : 100.0);
  const fvgFilterEnable = params.fvgFilterEnable === true || params.fvg_filter_enable === true || params.smcFvgFilterEnable === true || params.smc_fvg_filter_enable === true;

  // Fakeout Revert on Stop Loss parameters
  const smcRevertEnable = params.smcRevertEnable === true || params.smc_revert_enable === true;
  const smcRevertEntryMode = (params.smcRevertEntryMode || params.smc_revert_entry_mode || "MARKET").toUpperCase();
  const smcRevertRr = params.smcRevertRr !== undefined ? Number(params.smcRevertRr) : (params.smc_revert_rr !== undefined ? Number(params.smc_revert_rr) : 1.5);
  const smcRevertSlBufferPips = params.smcRevertSlBufferPips !== undefined ? Number(params.smcRevertSlBufferPips) : (params.smc_revert_sl_buffer_pips !== undefined ? Number(params.smc_revert_sl_buffer_pips) : 10.0);
  const smcRevertCancelBars = params.smcRevertCancelBars !== undefined ? Number(params.smcRevertCancelBars) : (params.smc_revert_cancel_bars !== undefined ? Number(params.smc_revert_cancel_bars) : 20);

  // Single order per side check (Chống nhồi lệnh liên tục - 1 lần chỉ đánh 1 lệnh cùng chiều)
  const smcSingleOrderPerSide = params.smcSingleOrderPerSide !== false && params.smc_single_order_per_side !== false;
  const smcMaxOrdersMode = (params.smcMaxOrdersMode || params.smc_max_orders_mode || (smcSingleOrderPerSide ? "ONE_PER_SIDE" : "UNLIMITED")).toUpperCase();

  // Smart Hybrid Primary Entry (Nếu SL <= threshold thì Market, nếu > threshold thì Limit hồi pullback %)
  const hybridThresholdPips = params.smcHybridThresholdPips !== undefined ? Number(params.smcHybridThresholdPips) : (params.smc_hybrid_threshold_pips !== undefined ? Number(params.smc_hybrid_threshold_pips) : (params.hybrid_threshold_pips !== undefined ? Number(params.hybrid_threshold_pips) : 80.0));
  const hybridPullbackPct = (params.smcHybridPullbackPct !== undefined ? Number(params.smcHybridPullbackPct) : (params.smc_hybrid_pullback_pct !== undefined ? Number(params.smc_hybrid_pullback_pct) : (params.hybrid_pullback_pct !== undefined ? Number(params.hybrid_pullback_pct) : 50.0))) / 100.0;

  const nBars = bars.length;
  const highs = bars.map(b => Number(b.high));
  const lows = bars.map(b => Number(b.low));
  const opens = bars.map(b => Number(b.open));
  const closes = bars.map(b => Number(b.close));
  const times = bars.map(b => Number(b.time));

  let htfOBs = [];
  let htfLookbackBars = 45;
  if (htfFilterEnable && nBars >= 30) {
    const detectedSec = nBars > 1 ? Math.max(60, times[1] - times[0]) : 300;
    let targetSec = 3600;
    if (htfTimeframe === "AUTO") {
      if (detectedSec <= 60) targetSec = 900;       // M1 -> M15
      else if (detectedSec <= 300) targetSec = 900;  // M5 -> M15
      else if (detectedSec <= 900) targetSec = 3600; // M15 -> H1
      else if (detectedSec <= 1800) targetSec = 14400; // M30 -> H4
      else if (detectedSec <= 3600) targetSec = 14400; // H1 -> H4
      else targetSec = 86400; // H4 -> D1
    } else {
      const tfMap = { "M5": 300, "M15": 900, "M30": 1800, "H1": 3600, "H4": 14400, "D1": 86400 };
      targetSec = tfMap[htfTimeframe] || (detectedSec <= 300 ? 900 : 3600);
    }
    if (targetSec > detectedSec) {
      const htfBars = resampleBarsToHTF(bars, targetSec);
      htfOBs = extractHTFOrderBlocks(htfBars, 3, 20);
    }
    const htfRatio = Math.max(1, Math.floor(targetSec / detectedSec));
    htfLookbackBars = Math.max(30, Math.min(120, htfRatio * 4));
  }

  const samplePrice = closes[nBars - 1];
  const mintick = getMintick(samplePrice);
  const pipMult = getPipMultiplier(samplePrice);
  const eps = epsTicks * mintick;
  const digits = samplePrice < 5.0 ? 5 : (samplePrice < 500.0 ? 3 : 2);
  const revSlBufPx = smcRevertSlBufferPips / pipMult;

  // Helper functions
  function slBufPx() {
    if (!slExtraOn) return 0.0;
    return slExtraPips / pipMult;
  }

  function fSlPrice(s, isBull) {
    if (s.reversed) return s.mid_p;
    const buf = slBufPx();
    return isBull ? (s.same_p - buf) : (s.same_p + buf);
  }

  function halfBreakMid(h, l, o, c) {
    return halfBreakBasis === "Range(H-L)" ? (h + l) / 2.0 : (o + c) / 2.0;
  }

  function fibZoneEntry(anchorP, midP, isSell) {
    const rng = Math.abs(anchorP - midP);
    const pct = Math.max(0.0, Math.min(1.0, fibPct / 100.0));
    return isSell ? (anchorP - rng * pct) : (anchorP + rng * pct);
  }

  function getBrkBuf(o, c) {
    const body = Math.abs(c - o);
    const bufPct = Math.max(body, mintick) * (breakBufPct * 0.01);
    const bufTicks = breakBufTicks * mintick;
    return breakMode === "%Body" ? bufPct : bufTicks;
  }

  function getRevBrkBuf(o, c) {
    const body = Math.abs(c - o);
    const revBufPctPx = Math.max(body, mintick) * (revBufPct * 0.01);
    const revBufTicksPx = revBufTicks * mintick;
    return revBreakMode === "%Body" ? revBufPctPx : revBufTicksPx;
  }

  function brkUp(c, h, o, level) {
    const buf = getBrkBuf(o, c);
    if (useClose) return breakAllowEqual ? (c >= level + buf) : (c > level + buf);
    return h > level + buf;
  }

  function brkDown(c, l, o, level) {
    const buf = getBrkBuf(o, c);
    if (useClose) return breakAllowEqual ? (c <= level - buf) : (c < level - buf);
    return l < level - buf;
  }

  function sameBreachUp(c, h, level) {
    return sameByClose ? (c >= level) : (h >= level);
  }

  function sameBreachDown(c, l, level) {
    return sameByClose ? (c <= level) : (l <= level);
  }

  function revBreachUp(c, h, o, level) {
    const buf = getRevBrkBuf(o, c);
    if (revByClose) return revAllowEqual ? (c >= level + buf) : (c > level + buf);
    return h > level + buf;
  }

  function revBreachDown(c, l, o, level) {
    const buf = getRevBrkBuf(o, c);
    if (revByClose) return revAllowEqual ? (c <= level - buf) : (c < level - buf);
    return l < level - buf;
  }

  function fLegExtremeFromBreak(isBuy, breakI, currI) {
    if (currI < breakI) return [null, null];
    let best = isBuy ? 1e100 : -1e100;
    let bestIdx = null;
    for (let b = breakI; b <= currI; b++) {
      const v = isBuy ? lows[b] : highs[b];
      if ((isBuy && v < best) || (!isBuy && v > best)) {
        best = v;
        bestIdx = b;
      }
    }
    return [best, bestIdx];
  }

  // Precompute ATR (14)
  const atr = new Float64Array(nBars);
  const tr = new Float64Array(nBars);
  tr[0] = highs[0] - lows[0];
  for (let i = 1; i < nBars; i++) {
    tr[i] = Math.max(
      highs[i] - lows[i],
      Math.max(Math.abs(highs[i] - closes[i - 1]), Math.abs(lows[i] - closes[i - 1]))
    );
  }
  let trSum = 0;
  for (let i = 0; i < nBars; i++) {
    trSum += tr[i];
    if (i >= 14) trSum -= tr[i - 14];
    atr[i] = trSum / Math.min(i + 1, 14);
  }

  function isUsSession(ts) {
    const date = new Date(ts > 1e11 ? ts : ts * 1000);
    const utcHours = date.getUTCHours();
    const utcMinutes = date.getUTCMinutes();
    const vnMinutes = ((utcHours + 7) % 24) * 60 + utcMinutes;
    return vnMinutes >= 19 * 60 && vnMinutes <= 23 * 60 + 30;
  }

  // MSB Engine State
  const bearSetups = [];
  const bullSetups = [];
  const finishedSetups = [];
  const orders = [];
  let pendingSpawn = null;
  const chartMarkers = [];
  const historicalSignals = [];

  const hasBlockingOrder = (side) => {
    if (smcMaxOrdersMode === "UNLIMITED") return false;
    if (smcMaxOrdersMode === "ONE_TOTAL") {
      if (pendingSpawn !== null) return true;
      return orders.some((o) => o.open && !o.closed);
    }
    // Default: ONE_PER_SIDE (1 lần chỉ đánh 1 lệnh cùng chiều, chưa TP/SL thì gồng lệnh đó)
    if (pendingSpawn !== null && pendingSpawn.side === side) return true;
    return orders.some((o) => o.open && !o.closed && o.side === side);
  };

  const startBar = Math.max(10, Math.max(lenPH, lenPL) * 2);

  for (let i = startBar; i < nBars; i++) {
    const currH = highs[i];
    const currL = lows[i];
    const currO = opens[i];
    const currC = closes[i];
    const currTime = times[i];

    let bearBreakThisBar = false;
    let bullBreakThisBar = false;

    // 0. Execute pending spawn from previous bar
    if (pendingSpawn !== null) {
      if (i >= pendingSpawn.at_bar) {
        const isMarketOrd = Boolean(pendingSpawn.is_market);
        const order = {
          id: `ord_${pendingSpawn.at_bar}_${pendingSpawn.side}_${pendingSpawn.time}`,
          side: pendingSpawn.side,
          entry: pendingSpawn.entry,
          sl: pendingSpawn.sl,
          tp: pendingSpawn.tp,
          tp2: pendingSpawn.tp2,
          open: true,
          closed: false,
          win: false,
          bar: pendingSpawn.at_bar,
          t_entry: times[pendingSpawn.at_bar] || currTime,
          closed_bar: null,
          t_close: null,
          pending: !isMarketOrd,
          filled: isMarketOrd,
          bar_filled: isMarketOrd ? pendingSpawn.at_bar : null,
          canceled: false,
          pnl_diff: 0.0,
          status: isMarketOrd ? "FILLED" : "PENDING",
          is_market: isMarketOrd
        };
        orders.push(order);
        pendingSpawn = null;
      }
    }

    // 1. Update Paper Orders Lifecycle
    const newRevertOrders = [];
    for (const o of orders) {
      // (1) Pending -> check TP hit first (cancel immediately), fill check, or timeout
      if (o.open && !o.closed && o.pending && !o.filled) {
        // HỦY LỆNH NẾU GIÁ CHẠM TP TRƯỚC KHI KHỚP LIMIT
        let hitTpFirst = false;
        if (!o.is_revert) {
          hitTpFirst = o.side === "BUY" ? (currH >= o.tp) : (currL <= o.tp);
        } else if (o.is_revert && o.bar < i) {
          hitTpFirst = o.side === "BUY" ? (currH >= o.tp) : (currL <= o.tp);
        }

        if (hitTpFirst) {
          o.open = false;
          o.closed = true;
          o.canceled = true;
          o.t_close = currTime;
          o.closed_bar = i;
          o.status = "CANCELED_TP_HIT";
          continue;
        }

        let hitEntry = false;
        if (tpSlByClose) {
          hitEntry = o.side === "BUY" ? (currC <= o.entry) : (currC >= o.entry);
        } else {
          hitEntry = o.side === "BUY" ? (currL <= o.entry) : (currH >= o.entry);
        }

        if (hitEntry) {
          o.filled = true;
          o.pending = false;
          o.bar_filled = i;
          o.status = "FILLED";
        } else {
          const isRev = Boolean(o.is_revert);
          const cancelThresh = isRev ? (o.cancel_bars || smcRevertCancelBars) : pendingCancelN;
          const shouldCancel = (isRev && (i - o.bar) >= cancelThresh) || (!isRev && pendingCancelOn && (i - o.bar) >= cancelThresh);
          if (shouldCancel) {
            o.open = false;
            o.closed = true;
            o.canceled = true;
            o.t_close = currTime;
            o.closed_bar = i;
            o.status = "CANCELED";
          }
        }
      }

      // (2) Filled -> check Breakeven & TP / SL
      if (o.open && !o.closed && o.filled) {
        // Check Breakeven trigger: when profit >= be_pips, move SL to entry price
        if (breakevenEnable && !o.is_breakeven) {
          if (o.side === "BUY") {
            if ((currH - o.entry) * pipMult >= breakevenPips) {
              o.sl = o.entry;
              o.is_breakeven = true;
            }
          } else { // SELL
            if ((o.entry - currL) * pipMult >= breakevenPips) {
              o.sl = o.entry;
              o.is_breakeven = true;
            }
          }
        }

        const canClose = allowSameBarExit || (o.bar_filled < i);
        if (canClose) {
          let hitTp = false;
          let hitSl = false;
          if (tpSlByClose) {
            if (o.side === "BUY") {
              hitTp = currC >= o.tp;
              hitSl = currC <= o.sl;
            } else {
              hitTp = currC <= o.tp;
              hitSl = currC >= o.sl;
            }
          } else {
            if (o.side === "BUY") {
              hitTp = currH >= o.tp;
              hitSl = currL <= o.sl;
            } else {
              hitTp = currL <= o.tp;
              hitSl = currH >= o.sl;
            }
          }

          if (hitSl || hitTp) {
            let winNow = hitTp && !hitSl;
            let isBeExit = false;
            if (hitSl && o.is_breakeven && Math.abs(o.sl - o.entry) < 1e-4) {
              isBeExit = true;
              winNow = false;
            }

            o.closed = true;
            o.open = false;
            o.win = winNow;
            o.closed_bar = i;
            o.t_close = currTime;
            if (isBeExit) {
              o.pnl_diff = 0.0;
              o.status = "BREAKEVEN";
            } else if (winNow) {
              o.pnl_diff = Math.abs(o.tp - o.entry);
              o.status = "WIN_TP";
            } else {
              o.pnl_diff = -Math.abs(o.entry - o.sl);
              o.status = "LOSS_SL";

              // Fakeout Revert on Stop Loss (Prevent duplicate overlapping revert orders in same direction)
              const revAction = o.side === "BUY" ? "SELL" : "BUY";
              const hasActiveRevert = newRevertOrders.some(ro => ro.side === revAction && (i - ro.bar <= 3)) ||
                                      hasBlockingOrder(revAction);
              if (smcRevertEnable && !o.is_revert && !hasActiveRevert) {
                let isRevMarket = false;
                let revEntry = currC;

                if (smcRevertEntryMode === "MARKET") {
                  revEntry = currC;
                  isRevMarket = true;
                } else if (smcRevertEntryMode === "LIMIT_MID") {
                  revEntry = (o.entry + o.sl) / 2.0;
                } else if (smcRevertEntryMode === "LIMIT_ENTRY") {
                  revEntry = o.entry;
                } else { // SMART_HYBRID
                  const distToSl = Math.abs(currC - o.sl) * pipMult;
                  if (distToSl <= (smcRevertSlBufferPips * 1.5)) {
                    revEntry = currC;
                    isRevMarket = true;
                  } else {
                    revEntry = (o.entry + o.sl) / 2.0;
                  }
                }

                if (revAction === "SELL") {
                  const baseline = isRevMarket ? o.sl : o.entry;
                  const revSl = Math.max(baseline, currH) + revSlBufPx;
                  const revRisk = revSl - revEntry;
                  if (revRisk > 0) {
                    const revTp = revEntry - revRisk * smcRevertRr;
                    const revOrd = {
                      id: `smc_rev_${currTime}_SELL`,
                      side: "SELL",
                      entry: Number(revEntry.toFixed(digits)),
                      sl: Number(revSl.toFixed(digits)),
                      tp: Number(revTp.toFixed(digits)),
                      tp2: Number(revTp.toFixed(digits)),
                      open: true,
                      closed: false,
                      win: false,
                      bar: i,
                      t_entry: currTime,
                      closed_bar: null,
                      t_close: null,
                      pending: !isRevMarket,
                      filled: isRevMarket,
                      bar_filled: isRevMarket ? i : null,
                      canceled: false,
                      pnl_diff: 0.0,
                      status: isRevMarket ? "FILLED" : "PENDING",
                      is_revert: true,
                      cancel_bars: smcRevertCancelBars
                    };
                    newRevertOrders.push(revOrd);
                    chartMarkers.push({
                      time: currTime,
                      position: "aboveBar",
                      color: "#f97316",
                      shape: "arrowDown",
                      text: `SMC REVERT SELL (${revEntry.toFixed(digits)})`
                    });
                    historicalSignals.push({
                      id: `smc_rev_sig_${currTime}`,
                      order_id: revOrd.id,
                      time: currTime,
                      bar_index: i,
                      action: "SELL",
                      type: "SMC REVERT SELL",
                      entry_mode: isRevMarket ? "MARKET" : "LIMIT",
                      price: Number(revEntry.toFixed(digits)),
                      sl: Number(revSl.toFixed(digits)),
                      tp1: Number(revTp.toFixed(digits)),
                      tp2: Number(revTp.toFixed(digits)),
                      rr_ratio: `1:${smcRevertRr.toFixed(1)}`,
                      confidence: 88,
                      is_revert: true,
                      reasons: [
                        `Đảo chiều Fakeout (Revert SELL sau khi BUY dính SL)`,
                        `Điểm vào Revert: ${revEntry.toFixed(digits)} (${isRevMarket ? 'Market' : 'Limit'})`,
                        `SL: ${revSl.toFixed(digits)} (Risk ${(revRisk * pipMult).toFixed(1)} pips)`
                      ],
                      status: isRevMarket ? "RUNNING" : "PENDING",
                      pnl_diff: 0.0
                    });
                  }
                } else { // BUY
                  const baseline = isRevMarket ? o.sl : o.entry;
                  const revSl = Math.min(baseline, currL) - revSlBufPx;
                  const revRisk = revEntry - revSl;
                  if (revRisk > 0) {
                    const revTp = revEntry + revRisk * smcRevertRr;
                    const revOrd = {
                      id: `smc_rev_${currTime}_BUY`,
                      side: "BUY",
                      entry: Number(revEntry.toFixed(digits)),
                      sl: Number(revSl.toFixed(digits)),
                      tp: Number(revTp.toFixed(digits)),
                      tp2: Number(revTp.toFixed(digits)),
                      open: true,
                      closed: false,
                      win: false,
                      bar: i,
                      t_entry: currTime,
                      closed_bar: null,
                      t_close: null,
                      pending: !isRevMarket,
                      filled: isRevMarket,
                      bar_filled: isRevMarket ? i : null,
                      canceled: false,
                      pnl_diff: 0.0,
                      status: isRevMarket ? "FILLED" : "PENDING",
                      is_revert: true,
                      cancel_bars: smcRevertCancelBars
                    };
                    newRevertOrders.push(revOrd);
                    chartMarkers.push({
                      time: currTime,
                      position: "belowBar",
                      color: "#f97316",
                      shape: "arrowUp",
                      text: `SMC REVERT BUY (${revEntry.toFixed(digits)})`
                    });
                    historicalSignals.push({
                      id: `smc_rev_sig_${currTime}`,
                      order_id: revOrd.id,
                      time: currTime,
                      bar_index: i,
                      action: "BUY",
                      type: "SMC REVERT BUY",
                      entry_mode: isRevMarket ? "MARKET" : "LIMIT",
                      price: Number(revEntry.toFixed(digits)),
                      sl: Number(revSl.toFixed(digits)),
                      tp1: Number(revTp.toFixed(digits)),
                      tp2: Number(revTp.toFixed(digits)),
                      rr_ratio: `1:${smcRevertRr.toFixed(1)}`,
                      confidence: 88,
                      is_revert: true,
                      reasons: [
                        `Đảo chiều Fakeout (Revert BUY sau khi SELL dính SL)`,
                        `Điểm vào Revert: ${revEntry.toFixed(digits)} (${isRevMarket ? 'Market' : 'Limit'})`,
                        `SL: ${revSl.toFixed(digits)} (Risk ${(revRisk * pipMult).toFixed(1)} pips)`
                      ],
                      status: isRevMarket ? "RUNNING" : "PENDING",
                      pnl_diff: 0.0
                    });
                  }
                }
              }
            }

            for (const sig of historicalSignals) {
              if (sig.order_id === o.id || (sig.time === o.t_entry && sig.action === o.side)) {
                sig.status = o.status;
                sig.pnl_diff = o.pnl_diff;
                sig.closed_time = o.t_close;
                break;
              }
            }
          }
        }
      }
    }

    for (const ro of newRevertOrders) {
      orders.push(ro);
    }

    // 2. Symmetric Pivots & Fast Pivots
    // pl_sym (lenPL, lenPL)
    let plSym = null;
    let plSymIdx = null;
    const chkPlIdx = i - lenPL;
    if (chkPlIdx >= lenPL) {
      let isPl = true;
      const val = lows[chkPlIdx];
      for (let k = chkPlIdx - lenPL; k <= chkPlIdx + lenPL; k++) {
        if (lows[k] < val) { isPl = false; break; }
      }
      if (isPl) { plSym = val; plSymIdx = chkPlIdx; }
    }

    // ph_sym (lenPH, lenPH)
    let phSym = null;
    let phSymIdx = null;
    const chkPhIdx = i - lenPH;
    if (chkPhIdx >= lenPH) {
      let isPh = true;
      const val = highs[chkPhIdx];
      for (let k = chkPhIdx - lenPH; k <= chkPhIdx + lenPH; k++) {
        if (highs[k] > val) { isPh = false; break; }
      }
      if (isPh) { phSym = val; phSymIdx = chkPhIdx; }
    }

    // ph_fast (lenPH, 1)
    let phFast = null;
    let phFastIdx = null;
    const chkPhfIdx = i - 1;
    if (chkPhfIdx >= lenPH) {
      let isPhf = true;
      const val = highs[chkPhfIdx];
      for (let k = chkPhfIdx - lenPH; k <= chkPhfIdx + 1; k++) {
        if (highs[k] > val) { isPhf = false; break; }
      }
      if (isPhf) { phFast = val; phFastIdx = chkPhfIdx; }
    }

    // pl_fast (lenPL, 1)
    let plFast = null;
    let plFastIdx = null;
    const chkPlfIdx = i - 1;
    if (chkPlfIdx >= lenPL) {
      let isPlf = true;
      const val = lows[chkPlfIdx];
      for (let k = chkPlfIdx - lenPL; k <= chkPlfIdx + 1; k++) {
        if (lows[k] < val) { isPlf = false; break; }
      }
      if (isPlf) { plFast = val; plFastIdx = chkPlfIdx; }
    }

    // Anchor triggers (Fast backwards early-exit scan)
    let newHH = true;
    let newLL = true;
    const winStart = Math.max(0, i - windowBars);
    for (let k = i - 1; k >= winStart; k--) {
      if (highs[k] >= currH) newHH = false;
      if (lows[k] <= currL) newLL = false;
      if (!newHH && !newLL) break;
    }

    // 3. BEAR SETUP PROCESSING
    if (newHH) {
      if (bearSetups.length >= maxSetups) {
        finishedSetups.push(bearSetups.shift());
      }
      bearSetups.push({
        id: `bear_${currTime}`,
        side: "BEAR",
        state: 1,
        anchor_p: currH,
        anchor_i: i,
        mid_p: null,
        mid_i: null,
        mid_locked: false,
        same_p: null,
        same_i: null,
        pre_high: null,
        pre_high_i: null,
        same_wait: false,
        break_i: null,
        reversed: false,
        broke: false,
        tp_drawn: false
      });
    }

    for (const s of [...bearSetups]) {
      if (s.state === 1 && i > s.anchor_i) {
        if (plSymIdx !== null && plSymIdx > s.anchor_i + minGapBars) {
          if (s.mid_p === null) {
            s.mid_p = plSym;
            s.mid_i = plSymIdx;
            s.state = 2;
          }
        }
      }

      if (s.state === 2 && !s.mid_locked) {
        if (plSymIdx !== null && plSymIdx > s.mid_i + minGapBars) {
          if (plSym <= s.mid_p - eps) {
            s.mid_p = plSym;
            s.mid_i = plSymIdx;
          }
        }
      }

      if (s.state === 2 && !s.mid_locked) {
        if (brkUp(currC, currH, currO, s.mid_p)) {
          s.mid_locked = true;
        }
      }

      if (s.state === 2 && s.same_p === null) {
        if (phFastIdx !== null && phFastIdx > s.mid_i + minGapBars) {
          s.same_p = phFast;
          s.same_i = phFastIdx;
          s.state = 3;
        }
      }

      if (s.state === 2 && i > s.mid_i + minGapBars + 1) {
        const hPrev = highs[i - 1];
        if (s.pre_high === null || hPrev > s.pre_high) {
          s.pre_high = hPrev;
          s.pre_high_i = i - 1;
        }
      }

      const bearBreakNow = (s.state >= 2) && brkDown(currC, currL, currO, s.mid_p);
      if (bearBreakNow && s.state === 2 && s.same_p === null) {
        if (s.pre_high !== null) {
          s.same_p = s.pre_high;
          s.same_i = s.pre_high_i;
          s.state = 3;
        }
      }

      if (s.state === 3 && !s.broke && !s.reversed) {
        if (s.same_p !== null && sameBreachUp(currC, currH, s.same_p)) {
          if (waitPivot) {
            s.same_p = null;
            s.same_i = null;
            s.same_wait = true;
          } else {
            if (highs[i - 1] >= (s.same_p !== null ? s.same_p : -1e10) + eps) {
              s.same_p = highs[i - 1];
              s.same_i = i - 1;
              s.same_wait = false;
            }
          }
        }

        if (refineByFast && phFastIdx !== null && (s.same_p === null || phFastIdx > s.same_i + minGapBars)) {
          if (s.same_p === null || phFast >= s.same_p + eps) {
            s.same_p = phFast;
            s.same_i = phFastIdx;
            s.same_wait = false;
          }
        }
      }

      const readyB = (s.state === 3) && (s.same_i !== null) && ((s.same_i - s.anchor_i) <= windowBars);
      const brokenB = readyB && brkDown(currC, currL, currO, s.mid_p) && !s.broke;

      if (brokenB) {
        s.broke = true;
        s.break_i = i;

        // ENTRY + SL FILTER (BEAR SELL)
        const entHalfBreak = halfBreakMid(currH, currL, currO, currC);
        const entMs = s.mid_p * msRatio + s.same_p * sameRatio;
        const slP = fSlPrice(s, false);

        const isBreakFvg = (i >= 2 && lows[i - 2] > highs[i]);
        const hasFvg = isBreakFvg || (Math.abs(currC - currO) >= 1.2 * atr[i]);
        const inUs = isUsSession(currTime);

        let entryP;
        let isMarketOrder = false;
        if (entryMode === "Smart Hybrid" || entryMode === "SMART_HYBRID" || entryMode === "Adaptive Smart Hybrid") {
          const distBreakSlPips = Math.abs(currC - slP) * pipMult;
          if (distBreakSlPips <= hybridThresholdPips) {
            entryP = currC;
            isMarketOrder = true;
          } else {
            entryP = currC + Math.abs(currC - slP) * hybridPullbackPct;
            isMarketOrder = false;
          }
        } else if (entryMode === "Limit 2" || entryMode === "Half Mid-Same" || entryMode === "Mid-Same") {
          entryP = entMs;
        } else { // Default: Limit 1 (Adaptive Smart Limit)
          if (inUs && hasFvg) {
            const distCandlePips = Math.abs(entHalfBreak - slP) * pipMult;
            if (!slFilterEnable || (distCandlePips >= slMinPips && distCandlePips <= slMaxPips)) {
              entryP = entHalfBreak;
            } else {
              entryP = entMs;
            }
          } else {
            entryP = entMs;
          }
        }

        const distPx = Math.abs(entryP - slP);
        const distPips = distPx * pipMult;
        const passSl = !slFilterEnable || (distPips >= slMinPips && distPips <= slMaxPips);

        const htfCheck = htfFilterEnable
          ? checkHTFConfirmation("SELL", i, entryP, htfOBs, highs, lows, times, htfBufferPips / pipMult, htfLookbackBars, htfMaxAgeDays * 86400, htfMode)
          : { confirmed: true, zone: null };
        const passHtf = !htfFilterEnable || htfCheck.confirmed;
        const passFvg = !fvgFilterEnable || isBreakFvg;

        const isBlocked = hasBlockingOrder("SELL");
        const passAllFilters = passSl && passHtf && passFvg && !isBlocked;

        s.entry_p = Number(entryP.toFixed(digits));
        s.sl_p = Number(slP.toFixed(digits));
        if (entryMode === "Smart Hybrid" || entryMode === "SMART_HYBRID" || entryMode === "Adaptive Smart Hybrid") {
          s.tp1_p = Number((entryP - Math.abs(entryP - slP) * (s.reversed ? revR : r1)).toFixed(digits));
          s.tp2_p = Number((entryP - Math.abs(entryP - slP) * r2).toFixed(digits));
        } else {
          s.tp1_p = Number((s.mid_p - Math.abs(s.same_p - s.mid_p) * (s.reversed ? revR : r1)).toFixed(digits));
          s.tp2_p = Number((s.mid_p - Math.abs(s.same_p - s.mid_p) * r2).toFixed(digits));
        }
        s.pass_sl = passSl;
        s.pass_htf = passHtf;
        s.pass_fvg = passFvg;
        s.spawned = Boolean(passAllFilters);

        if (passAllFilters && !bearBreakThisBar) {
          bearBreakThisBar = true;
          const rr1Val = s.reversed ? revR : r1;
          const tpP = s.tp1_p;
          const tp2P = s.tp2_p;

          const whenBar = i + (placeNextBar ? 1 : 0);
          const ordId = `ord_${whenBar}_SELL_${currTime}`;
          pendingSpawn = {
            side: "SELL",
            entry: s.entry_p,
            sl: s.sl_p,
            tp: tpP,
            tp2: tp2P,
            at_bar: whenBar,
            setup_ref: s.id,
            time: currTime,
            is_market: isMarketOrder
          };

          let markerText = `MSB SELL`;
          if (htfFilterEnable && fvgFilterEnable) {
            markerText = `MSB SELL [HTF+FVG ✓]`;
          } else if (htfFilterEnable) {
            markerText = `MSB SELL [HTF ✓]`;
          } else if (fvgFilterEnable) {
            markerText = `MSB SELL [FVG ✓]`;
          } else {
            markerText = `MSB SELL (Risk: ${distPips.toFixed(1)} pips)`;
          }

          chartMarkers.push({
            time: currTime,
            position: "aboveBar",
            color: "#ef4444",
            shape: "arrowDown",
            text: markerText
          });

          const reasons = [
            `Phá vỡ Lmid (${s.mid_p.toFixed(2)})`,
            `Hsame (${s.same_p.toFixed(2)})`,
            `Risk: ${distPips.toFixed(1)} pips hợp lệ`
          ];
          if (fvgFilterEnable && isBreakFvg) {
            reasons.unshift(`✨ Nến Breakout tạo FVG Imbalance`);
          }
          if (htfCheck.confirmed && htfCheck.zone) {
            reasons.unshift(`🛡️ Khung lớn ${htfCheck.zone.type} bảo vệ`);
          }

          historicalSignals.push({
            id: `msb_sig_${currTime}`,
            order_id: ordId,
            time: currTime,
            bar_index: i,
            action: "SELL",
            type: "MSB BREAK SELL",
            price: Number(entryP.toFixed(digits)),
            sl: Number(slP.toFixed(digits)),
            tp1: Number(tpP.toFixed(digits)),
            tp2: Number(tp2P.toFixed(digits)),
            rr_ratio: `1:${rr1Val}`,
            confidence: (htfFilterEnable || fvgFilterEnable) ? 95 : 88,
            htf_confirmed: htfCheck.confirmed,
            htf_zone: htfCheck.zone,
            fvg_confirmed: isBreakFvg,
            reasons: reasons,
            status: "RUNNING",
            pnl_diff: 0.0
          });
        }

        // Reversal (Bear -> Buy)
        if (revEnable) {
          const barsSinceBreak = i - s.break_i;
          if (barsSinceBreak >= revMinBars && barsSinceBreak <= revMaxBars && s.same_p !== null) {
            if (revBreachUp(currC, currH, currO, s.same_p)) {
              const newMidP = s.same_p;
              const newMidI = s.same_i;
              const [newSameP, newSameI] = fLegExtremeFromBreak(true, s.break_i, i);
              if (newSameP !== null && (newMidP - newSameP > eps)) {
                if (bullSetups.length >= maxSetups) {
                  finishedSetups.push(bullSetups.shift());
                }
                bullSetups.push({
                  id: `rev_bull_${currTime}`,
                  side: "BULL",
                  state: 3,
                  anchor_p: null,
                  anchor_i: s.anchor_i,
                  mid_p: newMidP,
                  mid_i: newMidI,
                  mid_locked: true,
                  same_p: newSameP,
                  same_i: newSameI,
                  same_wait: false,
                  pre_low: null,
                  pre_low_i: null,
                  break_i: null,
                  reversed: true,
                  broke: false,
                  tp_drawn: false
                });
              }
            }
          }
        }
      }
    }

    // 4. BULL SETUP PROCESSING
    if (newLL) {
      if (bullSetups.length >= maxSetups) {
        finishedSetups.push(bullSetups.shift());
      }
      bullSetups.push({
        id: `bull_${currTime}`,
        side: "BULL",
        state: 1,
        anchor_p: currL,
        anchor_i: i,
        mid_p: null,
        mid_i: null,
        mid_locked: false,
        same_p: null,
        same_i: null,
        pre_low: null,
        pre_low_i: null,
        same_wait: false,
        break_i: null,
        reversed: false,
        broke: false,
        tp_drawn: false
      });
    }

    for (const s of [...bullSetups]) {
      if (s.state === 1 && i > s.anchor_i) {
        if (phSymIdx !== null && phSymIdx > s.anchor_i + minGapBars) {
          if (s.mid_p === null) {
            s.mid_p = phSym;
            s.mid_i = phSymIdx;
            s.state = 2;
          }
        }
      }

      if (s.state === 2 && !s.mid_locked) {
        if (phSymIdx !== null && phSymIdx > s.mid_i + minGapBars) {
          if (phSym >= s.mid_p + eps) {
            s.mid_p = phSym;
            s.mid_i = phSymIdx;
          }
        }
      }

      if (s.state === 2 && !s.mid_locked) {
        if (brkDown(currC, currL, currO, s.mid_p)) {
          s.mid_locked = true;
        }
      }

      if (s.state === 2 && s.same_p === null) {
        if (plFastIdx !== null && plFastIdx > s.mid_i + minGapBars) {
          s.same_p = plFast;
          s.same_i = plFastIdx;
          s.state = 3;
        }
      }

      if (s.state === 2 && i > s.mid_i + minGapBars + 1) {
        const lPrev = lows[i - 1];
        if (s.pre_low === null || lPrev < s.pre_low) {
          s.pre_low = lPrev;
          s.pre_low_i = i - 1;
        }
      }

      const bullBreakNow = (s.state >= 2) && brkUp(currC, currH, currO, s.mid_p);
      if (bullBreakNow && s.state === 2 && s.same_p === null) {
        if (s.pre_low !== null) {
          s.same_p = s.pre_low;
          s.same_i = s.pre_low_i;
          s.state = 3;
        }
      }

      if (s.state === 3 && !s.broke && !s.reversed) {
        if (s.same_p !== null && sameBreachDown(currC, currL, s.same_p)) {
          if (waitPivot) {
            s.same_p = null;
            s.same_i = null;
            s.same_wait = true;
          } else {
            if (lows[i - 1] <= (s.same_p !== null ? s.same_p : 1e10) - eps) {
              s.same_p = lows[i - 1];
              s.same_i = i - 1;
              s.same_wait = false;
            }
          }
        }

        if (refineByFast && plFastIdx !== null && (s.same_p === null || plFastIdx > s.same_i + minGapBars)) {
          if (s.same_p === null || plFast <= s.same_p - eps) {
            s.same_p = plFast;
            s.same_i = plFastIdx;
            s.same_wait = false;
          }
        }
      }

      const readyU = (s.state === 3) && (s.same_i !== null) && ((s.same_i - s.anchor_i) <= windowBars);
      const brokenU = readyU && brkUp(currC, currH, currO, s.mid_p) && !s.broke;

      if (brokenU) {
        s.broke = true;
        s.break_i = i;

        // ENTRY + SL FILTER (BULL BUY)
        const entHalfBreak = halfBreakMid(currH, currL, currO, currC);
        const entMs = s.mid_p * msRatio + s.same_p * sameRatio;
        const slP = fSlPrice(s, true);

        const isBreakFvg = (i >= 2 && highs[i - 2] < lows[i]);
        const hasFvg = isBreakFvg || (Math.abs(currC - currO) >= 1.2 * atr[i]);
        const inUs = isUsSession(currTime);

        let entryP;
        let isMarketOrder = false;
        if (entryMode === "Smart Hybrid" || entryMode === "SMART_HYBRID" || entryMode === "Adaptive Smart Hybrid") {
          const distBreakSlPips = Math.abs(currC - slP) * pipMult;
          if (distBreakSlPips <= hybridThresholdPips) {
            entryP = currC;
            isMarketOrder = true;
          } else {
            entryP = currC - Math.abs(currC - slP) * hybridPullbackPct;
            isMarketOrder = false;
          }
        } else if (entryMode === "Limit 2" || entryMode === "Half Mid-Same" || entryMode === "Mid-Same") {
          entryP = entMs;
        } else { // Default: Limit 1 (Adaptive Smart Limit)
          if (inUs && hasFvg) {
            const distCandlePips = Math.abs(entHalfBreak - slP) * pipMult;
            if (!slFilterEnable || (distCandlePips >= slMinPips && distCandlePips <= slMaxPips)) {
              entryP = entHalfBreak;
            } else {
              entryP = entMs;
            }
          } else {
            entryP = entMs;
          }
        }

        const distPx = Math.abs(entryP - slP);
        const distPips = distPx * pipMult;
        const passSl = !slFilterEnable || (distPips >= slMinPips && distPips <= slMaxPips);

        const htfCheck = htfFilterEnable
          ? checkHTFConfirmation("BUY", i, entryP, htfOBs, highs, lows, times, htfBufferPips / pipMult, htfLookbackBars, htfMaxAgeDays * 86400, htfMode)
          : { confirmed: true, zone: null };
        const passHtf = !htfFilterEnable || htfCheck.confirmed;
        const passFvg = !fvgFilterEnable || isBreakFvg;

        const isBlocked = hasBlockingOrder("BUY");
        const passAllFilters = passSl && passHtf && passFvg && !isBlocked;

        s.entry_p = Number(entryP.toFixed(digits));
        s.sl_p = Number(slP.toFixed(digits));
        if (entryMode === "Smart Hybrid" || entryMode === "SMART_HYBRID" || entryMode === "Adaptive Smart Hybrid") {
          s.tp1_p = Number((entryP + Math.abs(entryP - slP) * (s.reversed ? revR : r1)).toFixed(digits));
          s.tp2_p = Number((entryP + Math.abs(entryP - slP) * r2).toFixed(digits));
        } else {
          s.tp1_p = Number((s.mid_p + Math.abs(s.same_p - s.mid_p) * (s.reversed ? revR : r1)).toFixed(digits));
          s.tp2_p = Number((s.mid_p + Math.abs(s.same_p - s.mid_p) * r2).toFixed(digits));
        }
        s.pass_sl = passSl;
        s.pass_htf = passHtf;
        s.pass_fvg = passFvg;
        s.spawned = Boolean(passAllFilters);

        if (passAllFilters && !bullBreakThisBar) {
          bullBreakThisBar = true;
          const rr1Val = s.reversed ? revR : r1;
          const tpP = s.tp1_p;
          const tp2P = s.tp2_p;

          const whenBar = i + (placeNextBar ? 1 : 0);
          const ordId = `ord_${whenBar}_BUY_${currTime}`;
          pendingSpawn = {
            side: "BUY",
            entry: s.entry_p,
            sl: s.sl_p,
            tp: tpP,
            tp2: tp2P,
            at_bar: whenBar,
            setup_ref: s.id,
            time: currTime,
            is_market: isMarketOrder
          };

          let markerText = `MSB BUY`;
          if (htfFilterEnable && fvgFilterEnable) {
            markerText = `MSB BUY [HTF+FVG ✓]`;
          } else if (htfFilterEnable) {
            markerText = `MSB BUY [HTF ✓]`;
          } else if (fvgFilterEnable) {
            markerText = `MSB BUY [FVG ✓]`;
          } else {
            markerText = `MSB BUY (Risk: ${distPips.toFixed(1)} pips)`;
          }

          chartMarkers.push({
            time: currTime,
            position: "belowBar",
            color: "#10b981",
            shape: "arrowUp",
            text: markerText
          });

          const reasons = [
            `Phá vỡ Hmid (${s.mid_p.toFixed(2)})`,
            `Lsame (${s.same_p.toFixed(2)})`,
            `Risk: ${distPips.toFixed(1)} pips hợp lệ`
          ];
          if (fvgFilterEnable && isBreakFvg) {
            reasons.unshift(`✨ Nến Breakout tạo FVG Imbalance`);
          }
          if (htfCheck.confirmed && htfCheck.zone) {
            reasons.unshift(`🛡️ Khung lớn ${htfCheck.zone.type} bảo vệ`);
          }

          historicalSignals.push({
            id: `msb_sig_${currTime}`,
            order_id: ordId,
            time: currTime,
            bar_index: i,
            action: "BUY",
            type: "MSB BREAK BUY",
            price: Number(entryP.toFixed(digits)),
            sl: Number(slP.toFixed(digits)),
            tp1: Number(tpP.toFixed(digits)),
            tp2: Number(tp2P.toFixed(digits)),
            rr_ratio: `1:${rr1Val}`,
            confidence: (htfFilterEnable || fvgFilterEnable) ? 95 : 88,
            htf_confirmed: htfCheck.confirmed,
            htf_zone: htfCheck.zone,
            fvg_confirmed: isBreakFvg,
            reasons: reasons,
            status: "RUNNING",
            pnl_diff: 0.0
          });
        }

        // Reversal (Bull -> Sell)
        if (revEnable) {
          const barsSinceBreak = i - s.break_i;
          if (barsSinceBreak >= revMinBars && barsSinceBreak <= revMaxBars && s.same_p !== null) {
            if (revBreachDown(currC, currL, currO, s.same_p)) {
              const newMidP = s.same_p;
              const newMidI = s.same_i;
              const [newSameP, newSameI] = fLegExtremeFromBreak(false, s.break_i, i);
              if (newSameP !== null && (newSameP - newMidP > eps)) {
                if (bearSetups.length >= maxSetups) {
                  finishedSetups.push(bearSetups.shift());
                }
                bearSetups.push({
                  id: `rev_bear_${currTime}`,
                  side: "BEAR",
                  state: 3,
                  anchor_p: null,
                  anchor_i: s.anchor_i,
                  mid_p: newMidP,
                  mid_i: newMidI,
                  mid_locked: true,
                  same_p: newSameP,
                  same_i: newSameI,
                  same_wait: false,
                  pre_high: null,
                  pre_high_i: null,
                  break_i: null,
                  reversed: true,
                  broke: false,
                  tp_drawn: false
                });
              }
            }
          }
        }
      }
    }
  }

  // 5. Backtest Calculations
  const completedOrders = orders.filter(o => o.closed && !o.canceled);
  const totalTrades = completedOrders.length;
  const wins = completedOrders.filter(o => o.win).length;
  const losses = completedOrders.filter(o => !o.win).length;
  const winRate = totalTrades > 0 ? Math.round((wins / totalTrades) * 1000) / 10 : 0.0;

  const grossProfit = completedOrders.filter(o => o.pnl_diff > 0).reduce((acc, o) => acc + o.pnl_diff, 0);
  const grossLoss = completedOrders.filter(o => o.pnl_diff < 0).reduce((acc, o) => acc + Math.abs(o.pnl_diff), 0);
  const profitFactor = grossLoss > 0 ? Math.round((grossProfit / grossLoss) * 100) / 100 : (grossProfit > 0 ? 2.8 : 1.0);

  // Streaks
  let curW = 0; let maxW = 0;
  let curL = 0; let maxL = 0;
  for (const o of completedOrders) {
    if (o.win) {
      curW += 1;
      maxW = Math.max(maxW, curW);
      curL = 0;
    } else {
      curL += 1;
      maxL = Math.max(maxL, curL);
      curW = 0;
    }
  }

  // Multi-window backtest (Based on Real-World Vietnam Calendar Days)
  const nowMs = Date.now();
  const nowVnDate = new Date(nowMs + 7 * 3600 * 1000);
  const vnYear = nowVnDate.getUTCFullYear();
  const vnMonth = nowVnDate.getUTCMonth();
  const vnDay = nowVnDate.getUTCDate();
  const todayStartTs = Math.floor(Date.UTC(vnYear, vnMonth, vnDay, 0, 0, 0) / 1000) - 7 * 3600;
  const threeDaysStartTs = todayStartTs - 2 * 24 * 3600;
  const sevenDaysStartTs = todayStartTs - 6 * 24 * 3600;
  const thirtyDaysStartTs = todayStartTs - 29 * 24 * 3600;

  const windowsConfig = {
    "24h": todayStartTs,
    "today": todayStartTs,
    "3d": threeDaysStartTs,
    "7d": sevenDaysStartTs,
    "30d": thirtyDaysStartTs,
    "all": null
  };
  const backtestWindows = {};
  for (const [winKey, startTsLimit] of Object.entries(windowsConfig)) {
    const winOrders = startTsLimit !== null
      ? completedOrders.filter(o => (o.t_close || o.t_entry || 0) >= startTsLimit)
      : completedOrders;

    const wCount = winOrders.filter(o => o.win).length;
    const lCount = winOrders.filter(o => !o.win).length;
    const tCount = wCount + lCount;
    const wRate = tCount > 0 ? Math.round((wCount / tCount) * 1000) / 10 : 0.0;

    const gp = winOrders.filter(o => o.pnl_diff > 0).reduce((acc, o) => acc + o.pnl_diff, 0);
    const gl = winOrders.filter(o => o.pnl_diff < 0).reduce((acc, o) => acc + Math.abs(o.pnl_diff), 0);
    const pf = gl > 0 ? Math.round((gp / gl) * 100) / 100 : (gp > 0 ? 2.8 : 1.0);
    const netP = winOrders.reduce((acc, o) => acc + o.pnl_diff, 0);

    let winCurW = 0, winMaxW = 0;
    let winCurL = 0, winMaxL = 0;
    winOrders.forEach(o => {
      if (o.win) {
        winCurW++;
        winMaxW = Math.max(winMaxW, winCurW);
        winCurL = 0;
      } else {
        winCurL++;
        winMaxL = Math.max(winMaxL, winCurL);
        winCurW = 0;
      }
    });

    backtestWindows[winKey] = {
      total_trades: tCount,
      win_rate: wRate,
      wins: wCount,
      losses: lCount,
      net_pnl: Math.round(netP * pipMult * 10) / 10,
      profit_factor: pf,
      avg_rr: `1:${r1}`,
      max_win_streak: winMaxW,
      max_loss_streak: winMaxL,
      streaks: {
        max_win_streak: winMaxW,
        max_loss_streak: winMaxL
      }
    };
  }

  // 6. Visual Lines for Canvas Overlay (Draw latest active setup & latest revert order)
  const visualLines = [];
  const allBroken = [...bullSetups, ...bearSetups, ...finishedSetups].filter(
    (s) => s.broke && s.entry_p !== undefined && s.entry_p !== null && s.spawned
  );
  allBroken.sort((a, b) => (a.break_i || 0) - (b.break_i || 0));

  const latestSetup = allBroken.length > 0 ? allBroken[allBroken.length - 1] : null;
  const setupsToDraw = latestSetup ? [latestSetup] : [];

  for (const s of setupsToDraw) {
    if (s.broke && s.entry_p !== undefined && s.entry_p !== null) {
      const startI = s.break_i !== null && s.break_i !== undefined ? s.break_i : (s.mid_i || 0);
      
      // If setup's order closed (e.g. SL or TP hit), stop lines at the closed bar
      const setupOrder = orders.find((o) => o.setup_ref === s.id);
      let endI = nBars - 1;
      let isClosed = false;
      if (setupOrder && setupOrder.closed && setupOrder.closed_bar !== null && setupOrder.closed_bar !== undefined) {
        endI = Math.min(nBars - 1, setupOrder.closed_bar);
        isClosed = true;
      }

      visualLines.push({
        id: `ln_entry_${s.id}`,
        type: "ENTRY",
        label: `ENTRY (${(entryMode === "Smart Hybrid" || entryMode === "SMART_HYBRID") ? (s.entry_p === closes[startI] ? "Market" : "Limit 50%") : (entryMode === "Limit 2" || entryMode === "Half Mid-Same" ? "Limit 2" : "Limit 1")})`,
        price: s.entry_p,
        start_time: times[startI],
        end_time: times[endI],
        is_closed: isClosed,
        color: "#facc15"
      });

      visualLines.push({
        id: `ln_tp1_${s.id}`,
        type: "TP1",
        label: "TP1",
        price: s.tp1_p,
        start_time: times[startI],
        end_time: times[endI],
        is_closed: isClosed,
        color: "#84cc16"
      });
      visualLines.push({
        id: `ln_tp2_${s.id}`,
        type: "TP2",
        label: "TP2",
        price: s.tp2_p,
        start_time: times[startI],
        end_time: times[endI],
        is_closed: isClosed,
        color: "#22c55e"
      });
      visualLines.push({
        id: `ln_sl_${s.id}`,
        type: "SL",
        label: "SL",
        price: s.sl_p,
        start_time: times[startI],
        end_time: times[endI],
        is_closed: isClosed,
        color: "#f97316"
      });
    }
  }

  // Draw Visual Lines for Active or Recent SMC REVERT Order
  const revertOrders = orders.filter((o) => o.is_revert);
  const latestRevertOrder = revertOrders.length > 0 ? revertOrders[revertOrders.length - 1] : null;

  if (latestRevertOrder && (latestRevertOrder.open || (nBars - 1 - (latestRevertOrder.bar || 0) <= 60))) {
    const revStartI = latestRevertOrder.bar !== null && latestRevertOrder.bar !== undefined ? latestRevertOrder.bar : (nBars - 1);
    const revIsClosed = Boolean(latestRevertOrder.closed && latestRevertOrder.closed_bar !== null && latestRevertOrder.closed_bar !== undefined);
    const revEndI = revIsClosed
      ? Math.min(nBars - 1, latestRevertOrder.closed_bar)
      : nBars - 1;
    const revSide = latestRevertOrder.side;
    const isRevLimit = latestRevertOrder.pending;
    const entryModeTag = isRevLimit ? "Limit" : "Market";

    visualLines.push({
      id: `ln_entry_${latestRevertOrder.id}`,
      type: "ENTRY",
      label: `ENTRY (REVERT ${revSide} ${entryModeTag})`,
      price: latestRevertOrder.entry,
      start_time: times[revStartI] || times[0],
      end_time: times[revEndI] || times[nBars - 1],
      is_closed: revIsClosed,
      color: "#f97316"
    });

    visualLines.push({
      id: `ln_tp1_${latestRevertOrder.id}`,
      type: "TP1",
      label: `TP1 (REVERT ${smcRevertRr}R)`,
      price: latestRevertOrder.tp,
      start_time: times[revStartI] || times[0],
      end_time: times[revEndI] || times[nBars - 1],
      is_closed: revIsClosed,
      color: "#10b981"
    });

    if (latestRevertOrder.tp2 && Math.abs(latestRevertOrder.tp2 - latestRevertOrder.tp) > 1e-4) {
      visualLines.push({
        id: `ln_tp2_${latestRevertOrder.id}`,
        type: "TP2",
        label: `TP2 (REVERT)`,
        price: latestRevertOrder.tp2,
        start_time: times[revStartI] || times[0],
        end_time: times[revEndI] || times[nBars - 1],
        is_closed: revIsClosed,
        color: "#22c55e"
      });
    }

    visualLines.push({
      id: `ln_sl_${latestRevertOrder.id}`,
      type: "SL",
      label: `SL (REVERT)`,
      price: latestRevertOrder.sl,
      start_time: times[revStartI] || times[0],
      end_time: times[revEndI] || times[nBars - 1],
      is_closed: revIsClosed,
      color: "#ef4444"
    });
  }

  let marketBias = "NEUTRAL";
  if (bullSetups.length > bearSetups.length) marketBias = "BULLISH";
  else if (bearSetups.length > bullSetups.length) marketBias = "BEARISH";

  // Synchronize order outcomes (WIN_TP, LOSS_SL, CANCELED, pnl_diff) back into historicalSignals
  const ordersMap = new Map();
  orders.forEach((o) => {
    if (o.id) ordersMap.set(o.id, o);
  });
  historicalSignals.forEach((sig) => {
    const o = ordersMap.get(sig.order_id);
    if (o) {
      sig.status = o.status || "RUNNING";
      sig.pnl_diff = o.pnl_diff || 0.0;
      sig.close_time = o.t_close;
    }
  });

  const latestSig = historicalSignals.length > 0 ? historicalSignals[historicalSignals.length - 1] : null;

  return {
    active_bear_setups: bearSetups.slice(-maxSetups),
    active_bull_setups: bullSetups.slice(-maxSetups),
    finished_setups: finishedSetups.slice(-10),
    visual_lines: visualLines,
    orders: orders,
    completed_trades: completedOrders,
    htf_obs: htfOBs,
    marketBias: marketBias,
    market_bias: marketBias,
    sentimentScore: marketBias === "BULLISH" ? 75 : (marketBias === "BEARISH" ? -75 : 0),
    backtest: backtestWindows["all"],
    backtest_windows: backtestWindows,
    signals: {
      latest_signal: latestSig,
      recent_signals: historicalSignals.slice(-20),
      all_signals: historicalSignals,
      chart_markers: chartMarkers,
      market_bias: marketBias,
      sentiment_score: marketBias === "BULLISH" ? 75 : (marketBias === "BEARISH" ? -75 : 0),
      backtest: backtestWindows["all"],
      backtest_windows: backtestWindows,
      streaks: {
        max_win_streak: maxW,
        max_loss_streak: maxL
      }
    }
  };
}

// Classical Smart Money Concepts (Order Blocks, FVG, BOS/CHoCH) for visual indicator overlay
export function calculateClassicalSMC(bars, params = {}) {
  const pivotLength = params.pivotLength || 5;
  const obLookback = params.obLookback || 20;
  const mitigationMethod = params.mitigationMethod || "close"; // "close" (Candle Close Through) or "wick" (Wick Touch)

  if (!bars || bars.length < 30) {
    return {
      orderBlocks: [],
      fvgZones: [],
      structureBreaks: [],
      swingPoints: []
    };
  }

  // Optimize: Window lookback up to 500 bars for instant real-time computation & replay
  const maxLookback = 500;
  const totalLen = bars.length;
  const offset = totalLen > maxLookback ? totalLen - maxLookback : 0;
  const activeBars = offset > 0 ? bars.slice(offset) : bars;

  const n = activeBars.length;
  const times = activeBars.map((b) => b.time);
  const closes = activeBars.map((b) => Number(b.close));
  const highs = activeBars.map((b) => Number(b.high));
  const lows = activeBars.map((b) => Number(b.low));
  const opens = activeBars.map((b) => Number(b.open));

  // ATR(14)
  const atrs = new Float64Array(n);
  let prevTr = highs[0] - lows[0];
  atrs[0] = prevTr;
  for (let i = 1; i < n; i++) {
    const tr = Math.max(
      highs[i] - lows[i],
      Math.max(Math.abs(highs[i] - closes[i - 1]), Math.abs(lows[i] - closes[i - 1]))
    );
    atrs[i] = (atrs[i - 1] * 13 + tr) / 14;
  }

  // 1. Swing Highs & Lows (O(N))
  const swingHighs = [];
  const swingLows = [];
  const swingPoints = [];
  const pl = Math.max(2, Math.min(pivotLength, 15));

  for (let i = pl; i < n - pl; i++) {
    let isHigh = true;
    let isLow = true;
    const hi = highs[i];
    const li = lows[i];

    for (let k = i - pl; k <= i + pl; k++) {
      if (k !== i) {
        if (highs[k] > hi) isHigh = false;
        if (lows[k] < li) isLow = false;
        if (!isHigh && !isLow) break;
      }
    }

    if (isHigh) {
      const sh = { index: i, time: times[i], price: hi, type: "HIGH" };
      swingHighs.push(sh);
      swingPoints.push(sh);
    }
    if (isLow) {
      const sl = { index: i, time: times[i], price: li, type: "LOW" };
      swingLows.push(sl);
      swingPoints.push(sl);
    }
  }

  // 2. Structure Breaks (BOS / CHoCH) - Fast pointer tracking O(N)
  const structureBreaks = [];
  let currentTrend = 0; // 1 = Bullish, -1 = Bearish
  let lastHighIdx = -1;
  let lastLowIdx = -1;
  let shPtr = 0;
  let slPtr = 0;
  let activeSH = null;
  let activeSL = null;

  for (let i = pl * 2; i < n; i++) {
    const currC = closes[i];
    const currTime = times[i];
    const maxIdx = i - pl;

    while (shPtr < swingHighs.length && swingHighs[shPtr].index < maxIdx) {
      activeSH = swingHighs[shPtr];
      shPtr++;
    }
    while (slPtr < swingLows.length && swingLows[slPtr].index < maxIdx) {
      activeSL = swingLows[slPtr];
      slPtr++;
    }

    if (!activeSH || !activeSL) continue;

    if (currC > activeSH.price && activeSH.index !== lastHighIdx) {
      const breakType = currentTrend === 1 ? "BOS" : "CHOCH";
      currentTrend = 1;
      lastHighIdx = activeSH.index;
      structureBreaks.push({
        id: `sb_bull_${currTime}`,
        type: breakType,
        bias: "BULLISH",
        price: activeSH.price,
        breakPrice: currC,
        fromTime: activeSH.time,
        time: currTime,
        barIndex: i
      });
    } else if (currC < activeSL.price && activeSL.index !== lastLowIdx) {
      const breakType = currentTrend === -1 ? "BOS" : "CHOCH";
      currentTrend = -1;
      lastLowIdx = activeSL.index;
      structureBreaks.push({
        id: `sb_bear_${currTime}`,
        type: breakType,
        bias: "BEARISH",
        price: activeSL.price,
        breakPrice: currC,
        fromTime: activeSL.time,
        time: currTime,
        barIndex: i
      });
    }
  }

  // 3. Fair Value Gaps (FVG) - Fast bounded scan
  const fvgZones = [];
  const startFvg = Math.max(2, n - 250);
  for (let i = startFvg; i < n; i++) {
    const prev2H = highs[i - 2];
    const prev2L = lows[i - 2];
    const currH = highs[i];
    const currL = lows[i];
    const currTime = times[i];
    const atrThresh = atrs[i] * 0.15;

    // Bullish FVG
    if (currL > prev2H && (currL - prev2H) >= atrThresh) {
      let isMitigated = false;
      let mitigatedTime = null;
      for (let k = i + 1; k < n; k++) {
        // "close": candle close breaks through FVG bottom; "wick": wick breaks through
        const hit = mitigationMethod === "close" ? (closes[k] <= prev2H) : (lows[k] <= prev2H);
        if (hit) {
          isMitigated = true;
          mitigatedTime = times[k];
          break;
        }
      }
      fvgZones.push({
        id: `fvg_bull_${currTime}`,
        type: "BULLISH_FVG",
        top: currL,
        bottom: prev2H,
        startTime: times[i - 2],
        endTime: isMitigated ? mitigatedTime : times[n - 1],
        is_mitigated: isMitigated,
        mitigatedTime: mitigatedTime
      });
    }

    // Bearish FVG
    if (currH < prev2L && (prev2L - currH) >= atrThresh) {
      let isMitigated = false;
      let mitigatedTime = null;
      for (let k = i + 1; k < n; k++) {
        // "close": candle close breaks through FVG top; "wick": wick breaks through
        const hit = mitigationMethod === "close" ? (closes[k] >= prev2L) : (highs[k] >= prev2L);
        if (hit) {
          isMitigated = true;
          mitigatedTime = times[k];
          break;
        }
      }
      fvgZones.push({
        id: `fvg_bear_${currTime}`,
        type: "BEARISH_FVG",
        top: prev2L,
        bottom: currH,
        startTime: times[i - 2],
        endTime: isMitigated ? mitigatedTime : times[n - 1],
        is_mitigated: isMitigated,
        mitigatedTime: mitigatedTime
      });
    }
  }

  // 4. Order Blocks (OB) - Fast scan
  const orderBlocks = [];
  const recentBreaks = structureBreaks.slice(-25);
  for (const sb of recentBreaks) {
    const bIdx = sb.barIndex;
    if (bIdx < 5) continue;

    if (sb.bias === "BULLISH") {
      let lowestIdx = bIdx - 1;
      let lowestLow = lows[lowestIdx];
      const scanLimit = Math.max(0, bIdx - obLookback);
      for (let k = bIdx - 1; k >= scanLimit; k--) {
        if (lows[k] < lowestLow) {
          lowestLow = lows[k];
          lowestIdx = k;
        }
      }
      if (lowestIdx >= 0) {
        let isMitigated = false;
        let mitigatedTime = null;
        const obTop = Math.max(opens[lowestIdx], closes[lowestIdx]);
        const obBottom = lows[lowestIdx];
        for (let m = bIdx + 1; m < n; m++) {
          // "close": candle close breaks through OB bottom; "wick": wick touches OB top
          const hit = mitigationMethod === "close" ? (closes[m] < obBottom) : (lows[m] <= obTop);
          if (hit) {
            isMitigated = true;
            mitigatedTime = times[m];
            break;
          }
        }
        orderBlocks.push({
          id: `ob_bull_${times[lowestIdx]}`,
          type: "BULLISH_OB",
          top: obTop,
          bottom: obBottom,
          startTime: times[lowestIdx],
          endTime: isMitigated ? mitigatedTime : times[n - 1],
          is_mitigated: isMitigated,
          mitigatedTime: mitigatedTime
        });
      }
    } else {
      let highestIdx = bIdx - 1;
      let highestHigh = highs[highestIdx];
      const scanLimit = Math.max(0, bIdx - obLookback);
      for (let k = bIdx - 1; k >= scanLimit; k--) {
        if (highs[k] > highestHigh) {
          highestHigh = highs[k];
          highestIdx = k;
        }
      }
      if (highestIdx >= 0) {
        let isMitigated = false;
        let mitigatedTime = null;
        const obTop = highs[highestIdx];
        const obBottom = Math.min(opens[highestIdx], closes[highestIdx]);
        for (let m = bIdx + 1; m < n; m++) {
          // "close": candle close breaks through OB top; "wick": wick touches OB bottom
          const hit = mitigationMethod === "close" ? (closes[m] > obTop) : (highs[m] >= obBottom);
          if (hit) {
            isMitigated = true;
            mitigatedTime = times[m];
            break;
          }
        }
        orderBlocks.push({
          id: `ob_bear_${times[highestIdx]}`,
          type: "BEARISH_OB",
          top: obTop,
          bottom: obBottom,
          startTime: times[highestIdx],
          endTime: isMitigated ? mitigatedTime : times[n - 1],
          is_mitigated: isMitigated,
          mitigatedTime: mitigatedTime
        });
      }
    }
  }

  return {
    orderBlocks: orderBlocks.slice(-20),
    fvgZones: fvgZones.slice(-25),
    structureBreaks: structureBreaks.slice(-20),
    swingPoints: swingPoints.slice(-30)
  };
}
